<template>
  <div class="trend" @mouseleave="hover = -1">
    <div class="legend" aria-hidden="true">
      <span v-for="item in series" :key="item.key"><i :style="{ background: item.color }" />{{ item.label }}</span>
    </div>
    <svg :width="width" :height="height" :viewBox="`0 0 ${width} ${height}`" role="img" :aria-label="`روند ${unit} ویزیت‌ها`" @mousemove="move" @click="pick">
      <g class="grid">
        <g v-for="tick in yTicks" :key="'y' + tick">
          <line :x1="plot.left" :x2="plot.right" :y1="y(tick)" :y2="y(tick)" />
          <text :x="plot.right + 8" :y="y(tick) + 4" text-anchor="start">{{ fa(tick) }}</text>
        </g>
      </g>
      <line class="baseline" :x1="plot.left" :x2="plot.right" :y1="plot.bottom" :y2="plot.bottom" />
      <text v-for="index in xTicks" :key="'x' + index" class="x-label" :x="x(index)" :y="height - 6" text-anchor="middle">{{ jDate(points[index].from) }}</text>
      <path v-if="series.length" class="area" :d="area(series[0].key)" :fill="series[0].color" />
      <path v-for="item in series" :key="item.key" class="line" :d="line(item.key)" :stroke="item.color" />
      <g v-if="hover >= 0">
        <line class="crosshair" :x1="x(hover)" :x2="x(hover)" :y1="plot.top" :y2="plot.bottom" />
        <circle v-for="item in series" :key="item.key" :cx="x(hover)" :cy="y(points[hover][item.key])" r="4.5" :fill="item.color" class="dot" />
      </g>
    </svg>
    <div v-if="hover >= 0" class="tip" :style="tipStyle" role="status">
      <span class="tip-title">{{ label(points[hover]) }}</span>
      <span v-for="item in series" :key="item.key" class="tip-row"><i :style="{ background: item.color }" /><b class="vn-num">{{ fa(points[hover][item.key]) }}</b> {{ item.label }}</span>
      <span v-if="points.length > 1" class="tip-hint">برای تمرکز روی این {{ step === 1 ? 'روز' : 'هفته' }} کلیک کنید</span>
    </div>
  </div>
</template>

<script>
import widthMixin from './widthMixin';
import { fa, jDate } from './format';

export default {
  name: 'TrendChart',
  mixins: [widthMixin],
  props: {
    points: { type: Array, required: true }, // [{ from, to, [series.key]: n }]
    series: { type: Array, required: true }, // [{ key, label, color }]
    step: { type: Number, default: 1 },
  },
  data: () => ({ height: 250, hover: -1 }),
  computed: {
    unit() { return this.step === 1 ? 'روزانه' : 'هفتگی'; },
    // Time reads right-to-left, matching the page direction; the value axis sits on the right.
    plot() { return { left: 12, right: this.width - 40, top: 16, bottom: this.height - 30 }; },
    band() { return this.points.length > 1 ? (this.plot.right - this.plot.left) / (this.points.length - 1) : 40; },
    max() { return Math.max(4, ...this.points.map(point => Math.max(...this.series.map(item => point[item.key] || 0)))); },
    yTicks() { const step = Math.max(1, Math.ceil(this.max / 4)); return [0, step, step * 2, step * 3, step * 4]; },
    xTicks() {
      const count = this.points.length;
      const every = Math.max(1, Math.ceil(count / Math.max(2, Math.floor(this.width / 90))));
      return Array.from({ length: count }, (_, i) => i).filter(i => (count - 1 - i) % every === 0);
    },
    tipStyle() {
      const left = this.x(this.hover);
      return left > this.width / 2 ? { right: (this.width - left + 14) + 'px' } : { left: (left + 14) + 'px' };
    },
  },
  methods: {
    fa, jDate,
    x(index) { return this.points.length > 1 ? this.plot.right - index * this.band : (this.plot.left + this.plot.right) / 2; },
    y(value) { return this.plot.bottom - ((value || 0) / this.yTicks[4]) * (this.plot.bottom - this.plot.top); },
    line(key) { return this.points.map((point, i) => `${i ? 'L' : 'M'}${this.x(i).toFixed(1)},${this.y(point[key]).toFixed(1)}`).join(''); },
    area(key) {
      if (!this.points.length) return '';
      return `${this.line(key)}L${this.x(this.points.length - 1).toFixed(1)},${this.plot.bottom}L${this.x(0).toFixed(1)},${this.plot.bottom}Z`;
    },
    nearest(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const index = Math.round((this.plot.right - (event.clientX - rect.left)) / this.band);
      return Math.min(this.points.length - 1, Math.max(0, index));
    },
    move(event) { if (this.points.length) this.hover = this.nearest(event); },
    pick(event) { if (this.points.length > 1) this.$emit('select', this.points[this.nearest(event)]); },
    label(point) { return this.step === 1 ? jDate(point.from, { weekday: 'long', month: 'long', day: 'numeric' }) : `هفته ${jDate(point.from)} تا ${jDate(point.to)}`; },
  },
};
</script>

<style scoped>
.trend { position: relative; width: 100%; }
.legend { display: flex; flex-wrap: wrap; gap: 16px; font-size: 12px; color: var(--text-2); margin-bottom: 6px; }
.legend span { display: inline-flex; align-items: center; gap: 6px; }
.legend i, .tip-row i { display: inline-block; width: 14px; height: 3px; }
svg { display: block; cursor: crosshair; overflow: visible; }
.grid line { stroke: var(--line); stroke-width: 1; }
.grid text, .x-label { fill: var(--text-3); font-size: 10.5px; font-family: var(--font-num); }
.baseline { stroke: var(--line-strong); stroke-width: 1; }
.line { fill: none; stroke-width: 2; stroke-linejoin: round; stroke-linecap: round; }
.area { opacity: .1; }
.crosshair { stroke: var(--text-4); stroke-width: 1; stroke-dasharray: 3 3; }
.dot { stroke: #fff; stroke-width: 2; }
.tip { position: absolute; top: 34px; z-index: 3; display: grid; gap: 3px; min-width: 160px; padding: 9px 11px; border-radius: var(--r-sm);
  background: var(--navy-900); color: var(--navy-200); font-size: 12px; box-shadow: var(--shadow-pop); pointer-events: none; }
.tip-title { font-weight: 800; color: #fff; margin-bottom: 2px; }
.tip-row { display: flex; align-items: center; gap: 6px; }
.tip-row b { color: #fff; font-size: 13px; }
.tip-hint { color: var(--shell-text-3); font-size: 10.5px; margin-top: 2px; }
</style>
